import { useState } from 'react';

import ArtDirectedImage from '@/components/Image/ArtDirectedImage';
import useStyles from '@/components/MediaOverlay/MediaOverlay.styles';
import { IVideoOverlay, VideoSources } from '@/components/MediaOverlay/types';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { useInteraction } from '@/hooks/useInteraction';
import useViewPortSize, { UseViewportSizeType } from '@/hooks/useViewportSize';

const getVideoUrl = (videos: VideoSources, viewport: UseViewportSizeType) => {
  if (viewport.viewportSize === 0) return undefined;
  if (viewport.isExtraSmall && videos.xs) return videos.xs;
  if (viewport.isSmall && videos.sm) return videos.sm;
  if (viewport.isMedium && videos.md) return videos.md;
  if (viewport.isLarge && videos.lg) return videos.lg;
  if (viewport.isExtraLarge && videos.xl) return videos.xl;

  return videos.fallback;
};

export default function VideoOverlay({
  mode,
  videos,
  images,
  darken,
  className,
  overlayClassName,
  ...htmlProps
}: IVideoOverlay) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isThumbnailVisible, setIsThumbnailVisible] = useState(true);
  const desktopOnlyOverlay = mode === 'desktopOnly';
  const { classes, cx } = useStyles({
    isThumbnailVisible,
    darken,
    desktopOnlyOverlay,
  });
  const viewportSize = useViewPortSize();

  const togglePause = () => setIsPlaying(false);
  const interactionHandlers = useInteraction(togglePause, ['Enter', ' ']);

  const videoUrl = getVideoUrl(videos, viewportSize);

  return (
    <div
      className={cx(classes.relative, className)}
      {...interactionHandlers}
      {...htmlProps}
    >
      <div className={cx(classes.relative, overlayClassName)}>
        <VideoPlayer
          url={videoUrl}
          className={cx(classes.videoPlayer, classes.videoPlayerZ)}
          autoplay={desktopOnlyOverlay ? !viewportSize.isMobile : true}
          controls={desktopOnlyOverlay ? viewportSize.isMobile : false}
          playing={isPlaying}
          onReady={() => setIsThumbnailVisible(false)}
          data-testid='overlay-video'
        />
        {images?.fallback[0] && (
          <ArtDirectedImage
            alt='presentation'
            images={images}
            pictureProps={{
              className: cx(classes.image, classes.imageZ),
            }}
            data-testid='overlay-image'
          />
        )}
      </div>
      <div className={cx(classes.overlayContent, classes.overlayContentZ)}>
        {htmlProps.children}
      </div>
    </div>
  );
}
